<template>
    <div
        v-if="Object.keys(cfObjects).length !== 0"
        class="crossfilter-control"
    >
        <forest-disturbances-dimension-chart
            v-for="dimension in dimensions"
            :key="`${dimension}-${dynamicKey}`"
            :dimension="dimension"
            :chart-data="cfObjects[dimension].data"
            :brush-selection="brushSelection"
            :bin-data="useBinData(dimension)"
            :x-axis-label="xAxisLabels[dimension]"
            @changeFilters="updateFilters"
        />

        <b-button
            class="reset-button"
            type="is-info"
            @click="resetFilters"
        >
            Reset Crossfilters
        </b-button>
    </div>
</template>

<script>
import crossfilter from 'crossfilter2';

import ForestDisturbancesDimensionChart from '@/components/charts/ForestDisturbancesDimensionChart.vue';

export default {
    name: 'CrossfilterControl',

    components: {
        ForestDisturbancesDimensionChart,
    },

    props: {
        crossfilterData: {
            type: Array,
            required: true,
        },

        initialMapZoom: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        cf: null,
        cfObjects: {},
        dimensions: ['type', 'year', 'bec_zone', 'aspect', 'elevation', 'slope'],
        brushSelection: [],
        dynamicKey: Date.now(),

        xAxisLabels: {
            year: 'Year',
            aspect: 'Aspect Ratio',
            bec_zone: 'Biogeoclimatic Zone',
            elevation: 'Elevation',
            slope: 'Average Slope',
            type: 'Forest Disturbance Type',
        },
    }),

    computed: {
        /**
         * Determine drop rate 4 constant modifier that uses the initial map zoom (floor)
         * and based on the map zoom level it returns a constant that can be multiplied
         * with the count of features to turn the count into area km2
         * https://docs.google.com/spreadsheets/d/1kTWZrgPWFiAm3UZLSuvY-9rJsMAjL7HUdTX0q4SDmQM/edit
         *
         * @returns {number} constant modifier to turn a count of features into area km2 based on map zoom level
         */
        dropRateModifier() {
            switch (Math.floor(this.initialMapZoom)) {
            case 0:
                return 60397.9776;
            case 1:
                return 15099.4944;
            case 2:
                return 3774.8736;
            case 3:
                return 943.7184;
            case 4:
                return 235.9296;
            case 5:
                return 58.9824;
            case 6:
                return 14.7456;
            case 7:
                return 3.6864;
            case 8:
                return 0.9216;
            case 9:
                return 0.2304;
            case 10:
                return 0.0144;
            case 11:
                return 0.0036;
            default:
                return 0.0009;
            }
        },
    },

    mounted() {
        this.init();
    },

    methods: {
        /**
         * Construct crossfilter with multi-dimensional dataset.
         */
        init() {
            // construct new crossfilter
            this.cf = crossfilter(this.crossfilterData);

            this.dimensions.forEach((dim) => {
                // constructs new dimension using specified value accessor function
                const dimension = this.cf.dimension((d) => d[dim]);

                // constructs new grouping for the given dimension
                const group = dimension.group((d) => d).reduce(this.reduceAdd, this.reduceRemove, this.reduceInitial);

                // group reduce histogram values and return objects contain 'key' and 'value' attributes
                const data = group.all();

                // construct crossfilter multi-dimensional dataset
                this.$set(this.cfObjects, dim, { dimension, group, data });
            });
        },

        /**
         * Update each dimension's filters and re-apply crossfilter.
         *
         * @param {Array} selection - brush selection position array
         * @param {string} dimension - updated forest disturbance dimension
         * @param {Array} filteredData - filtered/selected dimension data
         */
        updateFilters(selection, dimension, filteredData) {
            // filter selected dimension data
            const keys = filteredData.map((d) => d.key);
            this.cfObjects[dimension].dimension.filter((d) => keys.includes(d));

            // update crossfilter multi-dimensional dataset
            Object.entries(this.cfObjects).forEach((entry) => {
                const dim = entry[0];
                const { group } = entry[1];
                // re-group reduce each dimension data and return objects contain 'key' and 'value' attributes
                this.cfObjects[dim].data = group.all();
            });

            // update brush selection for chart re-render
            this.brushSelection = selection;

            // returns all of the raw records with filters applied
            this.$emit('crossfilter-update', this.cf.allFiltered());
        },

        /**
         * Reset crossfilters and re-init the charts so the brushing resets. Emit to parent so the data resets as well.
         */
        resetFilters() {
            this.$emit('reset-filters');
            this.dynamicKey = Date.now();
            this.init();
        },

        /**
         * Specify custom reduce add function for grouping when records are filtered.
         * https://github.com/crossfilter/crossfilter/wiki/API-Reference#group_reduce
         *
         * @param {number} count - current group reduced count
         * @returns {number} - group reduced count
         */
        reduceAdd(count) {
            // count gets multiplied by the drop rate modifier to convert into area km2
            // + symbol appears to convert into a multiplication within the crossfilter package
            return count + this.dropRateModifier;
        },

        /**
         * Specify custom reduce remove function for grouping when records are filtered.
         * https://github.com/crossfilter/crossfilter/wiki/API-Reference#group_reduce
         *
         * @param {number} count - current group reduced count
         * @returns {number} - group reduced count
         */
        reduceRemove(count) {
            // reverse of the reduceAdd function above
            // count gets divided? by the drop rate modifier
            // - symbol appears to convert into a division within the crossfilter package
            return count - this.dropRateModifier;
        },

        /**
         * Specify custom group reduce function with initial values.
         * https://github.com/crossfilter/crossfilter/wiki/API-Reference#group_reduce
         *
         * @returns {number} - initial group reduced count
         */
        reduceInitial() {
            return 0;
        },

        /**
         * Determine if the specified dimension should use binned data in the chart
         *
         * @param {string} dimension - dimension for chart in question
         * @returns {object} - containing if the data should be binned and a threshold count
         */
        useBinData(dimension) {
            switch (dimension) {
            case 'elevation':
                return {
                    useBinnedData: true,
                    binThreshold: 25,
                };
            case 'aspect':
                return {
                    useBinnedData: true,
                    binThreshold: 20,
                };
            default:
                return { useBinnedData: false };
            }
        },
    },
};
</script>

<style lang="scss">
.crossfilter-control {
    display: flex;
    flex-direction: column;
    align-items: center;

    .reset-button {
        margin-left: 2rem;
    }
}
</style>
